/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 20-05-2026
 *
 * Angular modal-component voor het bewerken van modeleigenschappen.
 * Vervangt de AngularJS modelPropsController + modelprops.html.
 */

import { Component, HostListener, inject, OnInit } from '@angular/core';
import { BaseIslandComponent } from '../../base-island.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import type { ModelPropsData, ModelPropsInitvalues, ModelPropsKeuze } from './model-props.types';

/** Definitie van één niveau-optie in de dropdown */
interface NiveauOptie {
    val: number;
    naam: string;
    icons?: string[];
    letters?: string[];
    disabled?: boolean;
}

@Component({
    selector: 'dl-model-props',
    standalone: true,
    imports: [CommonModule, FormsModule, TranslateModule],
    templateUrl: './dl-model-props.component.html',
    styleUrls: ['./dl-model-props.component.scss']
})
export class DlModelPropsComponent extends BaseIslandComponent implements OnInit {

    /** Initialisatiedata meegegeven via BsModalService initialState */
    initvalues!: ModelPropsInitvalues;

    /** Werkende kopie van de modeldata */
    model!: ModelPropsData;

    /** Vertaalde aanheftekst */
    aanhef = '';

    /** Of de titel-input uitgeschakeld is */
    titelDisabled = false;

    /** Of het modelleerniveau aanpasbaar is */
    modelleerniveauSchrijfbaar = false;

    /** Of de simulatiepreferentie aanpasbaar is */
    simulatiepreferentieSchrijfbaar = false;

    /** Of fastest_path aanpasbaar is */
    fastestPathOverschrijfbaar = false;

    /** Weergavetekst voor de project-simulatiepreferentie */
    projectSimulatiepreferentie = '';

    /** Weergavetekst voor de project-fastest_path instelling */
    projectFastestPath = '';

    /** Of de niveau-dropdown open is */
    selectOpen = false;

    /** Alle beschikbare niveaus */
    niveaus: NiveauOptie[] = [];

    /** Promise-resolve functie, gezet door de service die de modal opent */
    resolve!: (keuze: ModelPropsKeuze) => void;

    private readonly modalRef = inject(BsModalRef);
    private readonly translate = inject(TranslateService);

    constructor() { super(); }

    ngOnInit(): void {
        // Werkende kopie zodat annuleren geen effect heeft op het origineel
        this.model = { ...this.initvalues.model };
        this.titelDisabled = !this.initvalues.wijzigtitel;
        this.modelleerniveauSchrijfbaar = this.initvalues.modelleerniveauOverschrijfbaar;
        this.simulatiepreferentieSchrijfbaar = this.initvalues.simulatiepreferentieOverschrijfbaar;
        this.fastestPathOverschrijfbaar = this.initvalues.fastestPathOverschrijfbaar;

        this.aanhef = this.initvalues.aanhef
            ? this.translate.instant(this.initvalues.aanhef)
            : '';

        this.projectSimulatiepreferentie = this.initvalues.projectSimulatiepreferentie
            ? this.translate.instant('SIMULATIEPREFERENTIE_' + this.initvalues.projectSimulatiepreferentie).replace('&amp;', '&')
            : '';

        this.projectFastestPath = this.translate.instant(
            `FASTESTPATH.${this.initvalues.projectFastestPath ? 'AAN' : 'UIT'}`
        );

        this.bouwNiveaus();
    }

    /** Bouwt de lijst van beschikbare niveauopties op */
    private bouwNiveaus(): void {
        const pniveau = this.initvalues.projectModelleerniveau || 20;
        const __ = (key: string) => this.translate.instant(key);

        const niveauMap: Record<number, NiveauOptie> = {
            0:  { val: 0,  naam: `${__('ALS_PROJECT')} (${__('MODELLEERNIVEAU_' + pniveau)})` },
            10: { val: 10, naam: __('MODELLEERNIVEAU_10'), disabled: true },
            20: { val: 20, naam: __('MODELLEERNIVEAU_20'), icons: ['causal_plus.svg', 'causal_min.svg'] },
            25: { val: 25, naam: __('MODELLEERNIVEAU_25'), icons: ['causal_plus.svg', 'causal_min.svg', 'inequality_equal.svg'] },
            30: { val: 30, naam: __('MODELLEERNIVEAU_30'), icons: ['menuitems/waardenNieuw/quantityspace.svg'] },
            40: { val: 40, naam: __('MODELLEERNIVEAU_40'), icons: ['influence_positive.svg', 'proportionality_positive.svg'] },
            50: { val: 50, naam: __('MODELLEERNIVEAU_50'), letters: ['?', '!'] },
        };

        // Niveau 0 krijgt dezelfde iconen als het projectniveau
        niveauMap[0].icons = niveauMap[pniveau]?.icons;

        this.niveaus = Object.values(niveauMap);
    }

    /** Geeft het huidige niveauobject terug op basis van de modelwaarde */
    get huidigNiveau(): NiveauOptie {
        return this.niveaus.find(n => n.val === this.model.modelleerniveau) ?? this.niveaus[0];
    }

    /** Opent of sluit de niveau-dropdown */
    toggleNiveauselect(event: Event): void {
        if (this.modelleerniveauSchrijfbaar) {
            this.selectOpen = !this.selectOpen;
            event.stopPropagation();
        }
    }

    /** Verwerkt een klik op een niveau in de dropdown */
    niveauselectClick(niveau: NiveauOptie, event: Event): void {
        event.stopPropagation();
        if (!niveau.disabled) {
            this.selectOpen = false;
            this.model.modelleerniveau = niveau.val;
        }
    }

    /** Sluit de dropdown bij een klik buiten het component */
    @HostListener('document:click')
    onDocumentClick(): void {
        this.selectOpen = false;
    }

    /** Sluit de modal zonder resultaat (annuleren) */
    annuleer(): void {
        this.resolve(null);
        this.modalRef.hide();
    }

    /** Sluit de modal met de ingevulde modeldata */
    bevestig(): void {
        this.resolve({ ...this.model });
        this.modalRef.hide();
    }

    /** Of het formulier geldig is (titel minimaal 3 tekens, maximaal 90) */
    get formulierGeldig(): boolean {
        return (this.model.titel?.trim().length >= 3 && this.model.titel.trim().length <= 90);
    }
}
